import React, { useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { modeUIContext, showErrorNotification } from '@moderepo/mode-ui-state';
import { getErrorMessage, setLocalStorageItem, useQueryParams } from '@moderepo/mode-app-components';
import { ritzDataContext, setLoginInfo } from '@moderepo/mode-data-state-ritz';
import { ApiError, RitzAPI } from '@moderepo/mode-internal-apis';
import { RouteHelper } from '..';
import * as Constants from '../../utils/constants';
import { useRitzTranslation } from '../../utils/customHooks';
import { useGetURLParamEntityIdAsString } from '../../utils/utils';
import { getOrganization } from '../app/App';
import { NotAuthorized } from '../not-authorized/NotAuthorized';


/**
 * This is a External login callback handler for all service provider that we support.
 */
export const ExternalLoginCallback: React.FC = () => {
    const navigate = useNavigate();
    const queryParams = useQueryParams();

    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { dispatch:dataDispatch } = useContext(ritzDataContext);
    const routeHelper = RouteHelper.getInstance(navigate);
    const { trans } = useRitzTranslation(undefined);
    const code = queryParams.get('code');
    const state = queryParams.get('state');
    const providerId = useGetURLParamEntityIdAsString('providerId') ?? '';
    const authenticateAgentCalled = useRef<boolean>(false);
    const organization = getOrganization();


    useEffect(() => {
        // Check the URL params for code and state. If code and state params are not in the URL, redirect the agent to login page
        // Also make sure the serviceName in the URL is one of the services that we support
        if (!code || !state || !providerId) {
            // Missing 'token', 'code', or 'providerId' in the URL params, need to take agent to login page
            uiDispatch(showErrorNotification(trans('notif.invalid_callback_url'), undefined, 10000));
            routeHelper.gotoLoginPage(true);
        }
    }, [code, routeHelper, providerId, state, trans, uiDispatch, organization]);


    useEffect(() => {
        if (!organization) {
            return;
        }

        const onAuthenticateAgent = async () => {
            if (code && state) {
                authenticateAgentCalled.current = true;

                try {
                    const loginInfo = await RitzAPI.getInstance().authAgentWithExternalLogin(organization.id, providerId, code, state);
                    setLocalStorageItem(Constants.LS_KEY_LOGIN_INFO, loginInfo);
                    await dataDispatch(setLoginInfo(loginInfo));
                    // Do nothing, the user will be taken to the home page automatically when login info is added to the state
                } catch (error) {
                    uiDispatch(showErrorNotification(getErrorMessage((error as ApiError).code)));
                    routeHelper.gotoLoginPage(true);
                }
            }
        };

        if (!authenticateAgentCalled.current) {
            onAuthenticateAgent();
        }
    }, [dataDispatch, routeHelper, state, authenticateAgentCalled, code, providerId, uiDispatch, organization]);


    // If organization is undefined then that mean we weren't able to get organization based on baseUrl or orgId in the params
    if (!organization) {
        return <NotAuthorized />;
    }

    return (
        <></>
    );
};
